"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCart = void 0;
const app_1 = require("@obvio/app");
const generated_client_1 = require("@obvio/app/constants/generated.client");
const ui_1 = require("@obvio/ui");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const useUi_1 = require("./useUi");
const CART_PRODUCT_SELECT = {
    id: true,
    title: true,
    slug: true,
    price: true,
    regularPrice: true,
    lowestPriceBeforeSale: true,
    currency: true,
    image: true,
    productId: true,
    attributes: true,
    physical: true,
    merchant: {
        id: true,
        name: true,
    },
};
if (!('Merchant' in generated_client_1.SCHEMA_TYPES)) {
    delete generated_client_1.SCHEMA_TYPES.Merchant;
}
function useCart(options) {
    const { t } = (0, app_1.useTranslation)();
    const [, dispatch] = (0, useUi_1.useUi)();
    const [loadingIds, setLoadingIds] = (0, react_1.useState)(new Set());
    const { data: cartData, fetchMore, loading: loadingCart, } = (0, app_1.useQuery)('getCart', {
        select: {
            overstock: true,
            productVariantId: true,
            voucherId: true,
            quantity: true,
            type: true,
        },
        merge: (_oldData, newData) => newData,
        fetchPolicy: (options === null || options === void 0 ? void 0 : options.networkOnly) ? 'networkOnly' : 'cacheFirst',
    });
    const validateCart = (0, react_1.useCallback)((callback) => __awaiter(this, void 0, void 0, function* () {
        const data = yield fetchMore();
        const hasErrors = data.some((variant) => variant.overstock);
        callback(hasErrors);
    }), [fetchMore]);
    const setLoading = (0, react_1.useCallback)((id, state) => setLoadingIds((value) => {
        if (state) {
            value.add(id);
        }
        else {
            value.delete(id);
        }
        return value;
    }), []);
    if (!('Merchant' in generated_client_1.SCHEMA_TYPES)) {
        delete CART_PRODUCT_SELECT.merchant;
    }
    const { data: productsData, loading } = (0, app_1.useQuery)('getProductsOverview', {
        args: cartData
            ? {
                variantsIds: (0, utils_1.exclude)(cartData.map(({ productVariantId }) => productVariantId), null, undefined),
            }
            : null,
        select: CART_PRODUCT_SELECT,
    });
    const { data: vouchersData } = (0, app_1.useQuery)('getVoucherOrders', {
        select: {
            value: true,
            currency: true,
            physical: true,
            dedication: true,
            serviceId: true,
        },
    });
    const [addProductMutation] = (0, app_1.useMutation)('addToCart', {
        refetchQueries: ['getCart'],
    });
    const [removeProductMutation] = (0, app_1.useMutation)('removeFromCart', {
        refetchQueries: ['getCart'],
    });
    const [setQuantityMutation] = (0, app_1.useMutation)('updateCartQuantity', {
        refetchQueries: ['getCart'],
    });
    const [clearCart] = (0, app_1.useMutation)('clearCart');
    const removeProduct = (0, react_1.useCallback)((cartProductId) => __awaiter(this, void 0, void 0, function* () { return removeProductMutation({ cartProductId }); }), [removeProductMutation]);
    const memoizedData = (0, react_1.useMemo)(() => {
        var _a;
        return ({
            products: (_a = productsData === null || productsData === void 0 ? void 0 : productsData.filter((v) => v.currency).reduce((acc, product) => {
                var _a, _b;
                const cartVariant = cartData === null || cartData === void 0 ? void 0 : cartData.find((cart) => cart.productVariantId === product.id);
                return [
                    ...acc,
                    Object.assign(Object.assign({}, product), { currency: product.currency, quantity: (_a = cartVariant === null || cartVariant === void 0 ? void 0 : cartVariant.quantity) !== null && _a !== void 0 ? _a : 1, overstock: Boolean(cartVariant === null || cartVariant === void 0 ? void 0 : cartVariant.overstock), cartVariantId: (_b = cartVariant === null || cartVariant === void 0 ? void 0 : cartVariant.id) !== null && _b !== void 0 ? _b : '' }),
                ];
            }, [])) !== null && _a !== void 0 ? _a : [],
            vouchers: (vouchersData !== null && vouchersData !== void 0 ? vouchersData : []).map((voucher) => {
                var _a, _b, _c, _d;
                return (Object.assign(Object.assign({}, voucher), { quantity: (_b = (_a = cartData === null || cartData === void 0 ? void 0 : cartData.find((cart) => cart.voucherId === voucher.id)) === null || _a === void 0 ? void 0 : _a.quantity) !== null && _b !== void 0 ? _b : 1, cartVariantId: (_d = (_c = cartData === null || cartData === void 0 ? void 0 : cartData.find((cart) => cart.voucherId === voucher.id)) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : '' }));
            }),
        });
    }, [cartData, productsData, vouchersData]);
    const checkAvailability = (0, react_1.useCallback)((id, quantity, callback) => __awaiter(this, void 0, void 0, function* () {
        setLoading(id, true);
        try {
            const newQuantity = yield setQuantityMutation({
                quantity,
                cartProductId: id,
            });
            setLoading(id, false);
            if (newQuantity !== quantity) {
                ui_1.toast.basic(t('store.info.insufficientStock'));
            }
            if (newQuantity === 0) {
                yield removeProduct(id);
            }
            return callback === null || callback === void 0 ? void 0 : callback(newQuantity);
        }
        catch (_a) {
            setLoading(id, false);
        }
        return 0;
    }), [removeProduct, setLoading, setQuantityMutation, t]);
    const debouncedCheckAvailability = (0, app_1.useDebounce)(checkAvailability, 600);
    const setQuantity = (0, react_1.useCallback)((cartProductId, quantity, callback) => __awaiter(this, void 0, void 0, function* () {
        const item = Object.values(memoizedData)
            .flat()
            .find((v) => v.cartVariantId === cartProductId);
        const variantId = item === null || item === void 0 ? void 0 : item.cartVariantId;
        if (variantId) {
            void debouncedCheckAvailability(variantId, quantity, callback);
        }
        callback === null || callback === void 0 ? void 0 : callback(quantity);
    }), [debouncedCheckAvailability, memoizedData]);
    const addProduct = (0, react_1.useCallback)((productVariantId, callback) => __awaiter(this, void 0, void 0, function* () {
        yield addProductMutation({ productVariantId });
        callback === null || callback === void 0 ? void 0 : callback();
    }), [addProductMutation]);
    const total = (0, react_1.useMemo)(() => memoizedData.products.reduce((acc, element) => { var _a; return acc + element.quantity * ((_a = element.price) !== null && _a !== void 0 ? _a : 0); }, 0) +
        memoizedData.vouchers.reduce((acc, element) => acc + element.value * element.quantity, 0), [memoizedData]);
    const openCart = (0, react_1.useCallback)(() => dispatch('OPEN_CART'), [dispatch]);
    const itemsType = (0, react_1.useMemo)(() => {
        const allItems = Object.values(memoizedData).flat();
        if (allItems.every((item) => item.physical)) {
            return 'PHYSICAL';
        }
        else if (allItems.every((item) => !item.physical)) {
            return 'DIGITAL';
        }
        return 'MIXED';
    }, [memoizedData]);
    return {
        addProduct,
        clearCart,
        data: memoizedData,
        loading: loading || loadingCart,
        removeProduct,
        setQuantity,
        total,
        loadingIds,
        validateCart,
        openCart,
        itemsType,
    };
}
exports.useCart = useCart;
